import { useDeferredValue, useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { PersonActivityStatus, type PersonActivityStatusValue } from "@convex/schemaEnums";
import { oneOf, searchUpdate, text } from "@/lib/search";
import { FilterCount } from "../components/FilterCount";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import type { Id } from "@convex/_generated/dataModel";
import { createColumnHelper } from "@tanstack/react-table";
import {
  LuCalendarDays,
  LuSquareCheck,
  LuListChecks,
  LuUserCog,
  LuMail,
  LuSearch,
  LuSend,
  LuTrash2,
  LuUsersRound,
} from "react-icons/lu";
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CloseButton,
  Dialog,
  Field,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  NativeSelect,
  Portal,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react";
import { api } from "@convex/_generated/api";
import { toastError, toastSuccess } from "../components/ui/toaster";
import { useViewerAccess } from "@/lib/access";
import { AssignmentAdminMenu, PersonAdminDialog } from "./ActivityAdminActions";
import { AssignActivityDrawer } from "./ActivityAssignDrawer";
import { TableSkeleton } from "../components/TableSkeleton";
import { PageHeader } from "../components/PageHeader";
import { DrawerSidebar } from "../components/DrawerSidebar";
import { DataTable } from "../components/DataTable";
import {
  ActivityAdminAccessDenied,
  ActivityAdminLoadingState,
  formatDay,
  tagStyle,
  type PersonActivityRow,
  type PersonOption,
  type ActivitySummary,
} from "./ActivityAdminShared";

const breadcrumbs = [
  { label: "CeMMbase", to: "/" },
  { label: "Activities", to: "/activities" },
  { label: "Assigned activities" },
];


const personActivityColumnHelper = createColumnHelper<PersonActivityRow>();

// Defined once, outside the component. An inline arrow is a new
// function on every render, and a table told its row identity has
// changed cannot skip anything.
const getRowId = (row: PersonActivityRow) => String(row._id);

export type AssignmentsSearch = {
  q?: string;
  status?: PersonActivityStatusValue;
  /** An activity's title: copies are matched by what they were assigned as. */
  activity?: string;
};

export function validateAssignmentsSearch(search: Record<string, unknown>): AssignmentsSearch {
  return {
    q: text(search.q),
    status: oneOf(search.status, Object.values(PersonActivityStatus)),
    activity: text(search.activity),
  };
}

export function ActivityAssignmentsPage() {
  const access = useViewerAccess();
  const isActivityAdmin = access?.isActivityAdmin ?? false;

  const catalogue = useQuery(api.activities.listActivities, isActivityAdmin ? {} : "skip") as ActivitySummary[] | undefined;
  const directory = useQuery(api.persons.listCeMMies, isActivityAdmin ? {} : "skip");
  const persons: PersonOption[] | undefined = directory?.persons;
  const personActivityRows = useQuery(api.personActivities.listAllForAdmin, isActivityAdmin ? {} : "skip") as PersonActivityRow[] | undefined;

  const deleteAssignment = useMutation(api.personActivities.deletePersonActivity);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [personActionsOpen, setPersonActionsOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // In the address, so a narrowed list can be linked to and survives a reload.
  const params = useSearch({ from: "/_authed/activities/admin/assigned" });
  const statusFilter = params.status ?? "all";
  const activityFilter = params.activity ?? "all";
  const search = params.q ?? "";
  const navigate = useNavigate({ from: "/activities/admin/assigned" });
  const setParams = (patch: AssignmentsSearch) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), ...searchUpdate });
  const isFiltered = statusFilter !== "all" || activityFilter !== "all" || search.trim() !== "";

  // The input stays immediate; the filtering lags behind it. Typing "gold"
  // used to re-filter four hundred rows and rebuild the table four times, so
  // the field itself stuttered -- the one thing that must never lag, because
  // the person is looking straight at it. React keeps the last result on
  // screen until the new one is ready.
  const deferredSearch = useDeferredValue(search);

  const filteredRows = useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();
    return (personActivityRows ?? []).filter((assignment) => {
      const matchesStatus = statusFilter === "all" || assignment.status === statusFilter;
      const matchesActivity = activityFilter === "all" || assignment.titleSnapshot === activityFilter;
      const matchesSearch = query.length === 0
        || assignment.titleSnapshot.toLowerCase().includes(query)
        || assignment.personName.toLowerCase().includes(query)
        || assignment.personEmail.toLowerCase().includes(query);
      return matchesStatus && matchesActivity && matchesSearch;
    });
  }, [personActivityRows, deferredSearch, statusFilter, activityFilter]);




  async function handleDeleteAssignment() {
    if (!confirmDeleteId) return;
    try {
      await deleteAssignment({ personActivityId: confirmDeleteId as Id<"personActivities"> });
      toastSuccess("Assignment deleted.");
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Deleting assignment failed.");
    } finally {
      setConfirmDeleteId(null);
    }
  }

  const confirmDeleteRow = confirmDeleteId
    ? (personActivityRows ?? []).find((a) => String(a._id) === confirmDeleteId)
    : null;

  const personActivityColumns = useMemo(
    () => [
      personActivityColumnHelper.accessor("titleSnapshot", {
        header: "Activity",
        // The title is the way in. The table lists assignments and every other
        // view of one is reachable by clicking it; this was the one place that
        // showed an assignment and offered no way to open it.
        cell: ({ row, getValue }) => (
          <Stack gap={1}>
            <Link
              to="/activities/$personActivityId"
              params={{ personActivityId: String(row.original._id) }}
            >
              <Text fontWeight="semibold" _hover={{ textDecoration: "underline" }}>
                {getValue()}
              </Text>
            </Link>
            <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">Assignment</Text>
          </Stack>
        ),
      }),
      personActivityColumnHelper.accessor("personName", {
        header: "Person",
        cell: ({ row }) => (
          <Stack gap={1}>
            <Text fontWeight="medium">{row.original.personName}</Text>
            <HStack gap={2} fontSize="sm" color="fg.muted">
              <Icon as={LuMail} boxSize={4} />
              {row.original.personEmail}
            </HStack>
          </Stack>
        ),
      }),
      personActivityColumnHelper.accessor("status", {
        header: "Status",
        cell: ({ getValue }) => (
          <Badge variant="outline" textTransform="uppercase" letterSpacing="0.18em">
            {getValue().replaceAll("_", " ")}
          </Badge>
        ),
      }),
      personActivityColumnHelper.accessor("dueDay", {
        header: "Due date",
        cell: ({ getValue }) => (
          <HStack gap={2} fontSize="sm" color="fg.muted">
            <Icon as={LuCalendarDays} boxSize={4} />
            {formatDay(getValue())}
          </HStack>
        ),
      }),
      personActivityColumnHelper.accessor("required", {
        header: "Scope",
        cell: ({ getValue }) => (
          <Badge variant={getValue() ? "solid" : "subtle"} colorPalette="gray" textTransform="uppercase" letterSpacing="0.18em">
            {getValue() ? "Required" : "Encouraged"}
          </Badge>
        ),
      }),
      personActivityColumnHelper.display({
        id: "tags",
        header: "Tags",
        cell: ({ row }) => (
          <HStack flexWrap="wrap" gap={2}>
            {row.original.tagSnapshots.length === 0 ? (
              <Badge variant="subtle" colorPalette="gray" textTransform="uppercase" letterSpacing="0.18em">No tags</Badge>
            ) : row.original.tagSnapshots.map((tag) => (
              <Badge key={tag.tagId} variant="outline" textTransform="uppercase" letterSpacing="0.18em" style={tagStyle(tag.color)}>
                {tag.name}
              </Badge>
            ))}
          </HStack>
        ),
      }),
      personActivityColumnHelper.display({
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <AssignmentAdminMenu
            row={row.original}
            persons={persons}
            onDelete={() => setConfirmDeleteId(String(row.original._id))}
          />
        ),
      }),
    ],
    // `persons` is read by the reassign picker in the actions cell. With an
    // empty list here the columns were built once and would have captured it
    // while it was still undefined, leaving the picker permanently empty.
    [persons],
  );

  if (access === undefined) {
    return <ActivityAdminLoadingState title="Assigned activities" description="Checking your access to the assignment administration workspace." breadcrumbs={breadcrumbs} />;
  }

  if (!isActivityAdmin) {
    return <ActivityAdminAccessDenied title="Assigned activities" description="This page is for activity admins." breadcrumbs={breadcrumbs} />;
  }

  return (
    <>
      <Box minH="full" bg="bg.subtle">
        <PageHeader
          title="Assigned activities"
          description="Every activity assigned to someone, with its status and due date."
          breadcrumbs={breadcrumbs}
          actions={
            <HStack flexWrap="wrap" gap={3}>
              <Badge variant="outline" px={4} py={2} textTransform="uppercase" letterSpacing="0.2em">{(personActivityRows ?? []).length} assignments</Badge>
              <Button variant="outline" onClick={() => setPersonActionsOpen(true)}>
                <LuUserCog />
                Act on a person
              </Button>
              <Button colorPalette="blue" onClick={() => setDrawerOpen(true)}>
                <LuListChecks />
                Assign activities
              </Button>
            </HStack>
          }
        />

        <Stack gap={6} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>

          {/* The controls straight above the table, as on every other
              filtered table; the count with the table it describes. */}
          <Stack direction={{ base: "column", lg: "row" }} gap={3}>
            <InputGroup flex="1" startElement={<Icon as={LuSearch} boxSize={4} color="fg.muted" />}>
              <Input
                size="sm"
                placeholder="Search by activity, person, or email"
                value={search}
                onChange={(event) => setParams({ q: event.target.value === "" ? undefined : event.target.value })}
              />
            </InputGroup>
            <NativeSelect.Root size="sm" w={{ base: "full", lg: "14rem" }}>
              <NativeSelect.Field
                aria-label="Status"
                value={statusFilter}
                onChange={(event) => {
                  const value = event.target.value;
                  setParams({ status: value === "all" ? undefined : value as PersonActivityStatusValue });
                }}
              >
                <option value="all">All statuses</option>
                {[...new Map((personActivityRows ?? []).map((assignment) => [assignment.status, assignment.statusLabel]))].map(([status, label]) => (
                  <option key={status} value={status}>{label}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            <NativeSelect.Root size="sm" w={{ base: "full", lg: "18rem" }}>
              <NativeSelect.Field
                aria-label="Activity"
                value={activityFilter}
                onChange={(event) => {
                  const value = event.target.value;
                  setParams({ activity: value === "all" ? undefined : value });
                }}
              >
                <option value="all">All activities</option>
                {[...new Set((personActivityRows ?? []).map((assignment) => assignment.titleSnapshot))].sort().map((title) => (
                  <option key={title} value={title}>{title}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Stack>

          {personActivityRows === undefined ? (
            <TableSkeleton rows={9} columns={6} />
          ) : (
            <Stack gap={2}>
            <FilterCount
              shown={filteredRows.length}
              total={personActivityRows.length}
              noun={["assignment", "assignments"]}
              filtered={isFiltered}
              onClear={() => void navigate({ search: {}, ...searchUpdate })}
            />
            <Box as="section" borderWidth="1px" borderColor="border" shadow="sm">
              <DataTable
                data={filteredRows}
                columns={personActivityColumns}
                getRowId={getRowId}
                emptyMessage="No assigned activities match the current filters."
                stickyHeader
                // The seeds made this table four hundred rows, and every one
                // of them went into the DOM before anything appeared. A small
                // first batch paints immediately; the rest follow.
                progressive
                initialBatchSize={20}
                progressiveBatchSize={500}
              />
            </Box>
            </Stack>
          )}
        </Stack>
      </Box>

      <AssignActivityDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        catalogue={catalogue}
        persons={persons}
      />

      <PersonAdminDialog
        open={personActionsOpen}
        onClose={() => setPersonActionsOpen(false)}
        persons={persons}
        rows={personActivityRows}
      />

      {/* ── Delete confirmation modal ── */}
      <Dialog.Root open={Boolean(confirmDeleteId)} onOpenChange={(e) => { if (!e.open) setConfirmDeleteId(null); }} role="alertdialog">
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxW="md">
              <Dialog.Header>
                <Dialog.Title color="red.500">Delete assignment</Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body>
                <Text>
                  Are you sure you want to permanently delete the assignment
                  {confirmDeleteRow ? (
                    <>
                      {" "}<Text as="strong" fontWeight="bold">{confirmDeleteRow.titleSnapshot}</Text> for{" "}
                      <Text as="strong" fontWeight="bold">{confirmDeleteRow.personName}</Text>
                    </>
                  ) : null}
                  ? This will also remove all related steps and reviews. This action cannot be undone.
                </Text>
              </Dialog.Body>
              <Dialog.Footer>
                <ButtonGroup gap={2}>
                  <Button variant="ghost" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
                  <Button colorPalette="red" onClick={() => void handleDeleteAssignment()}>
                    <LuTrash2 />
                    Delete permanently
                  </Button>
                </ButtonGroup>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
}
